export interface AdditionFact {
  addend1: number;
  addend2: number;
  sum: number;
}

export interface LevelData {
  level: number;
  name: string;
  rule: string;
  ruleAudioText: string;
  ruleExampleAudioText?: string;
  primaryFacts: AdditionFact[];
  commutativeFacts: AdditionFact[];
  visualization?: 'number-line' | 'ten-frame' | 'five-frame' | 'beads' | 'hand';
}

export const CURRICULUM: LevelData[] = [
  // Level 1: Plus 1 (2-5)
  {
    level: 1,
    name: 'Plus 1 (2-5)',
    rule: 'When you plus one, you say the next number.',
    ruleAudioText: 'When you plus one, you say the next number.',
    ruleExampleAudioText:
      'When you do two plus one, the answer is the next number, which is three.',
    primaryFacts: [
      { addend1: 2, addend2: 1, sum: 3 },
      { addend1: 3, addend2: 1, sum: 4 },
      { addend1: 4, addend2: 1, sum: 5 },
      { addend1: 5, addend2: 1, sum: 6 },
    ],
    commutativeFacts: [
      { addend1: 1, addend2: 2, sum: 3 },
      { addend1: 1, addend2: 3, sum: 4 },
      { addend1: 1, addend2: 4, sum: 5 },
      { addend1: 1, addend2: 5, sum: 6 },
    ],
    visualization: 'number-line',
  },

  // Level 2: Plus 1 (6-9)
  {
    level: 2,
    name: 'Plus 1 (6-9)',
    rule: 'When you plus one, you say the next number.',
    ruleAudioText: 'When you plus one, you say the next number.',
    ruleExampleAudioText:
      'When you do six plus one, the answer is the next number, which is seven.',
    primaryFacts: [
      { addend1: 6, addend2: 1, sum: 7 },
      { addend1: 7, addend2: 1, sum: 8 },
      { addend1: 8, addend2: 1, sum: 9 },
      { addend1: 9, addend2: 1, sum: 10 },
    ],
    commutativeFacts: [
      { addend1: 1, addend2: 6, sum: 7 },
      { addend1: 1, addend2: 7, sum: 8 },
      { addend1: 1, addend2: 8, sum: 9 },
      { addend1: 1, addend2: 9, sum: 10 },
    ],
    visualization: 'number-line',
  },

  // Level 3: Plus 2 (2-5)
  {
    level: 3,
    name: 'Plus 2 (2-5)',
    rule: 'When you plus two, you skip one number and say the next one.',
    ruleAudioText: 'When you plus two, you skip one number and say the next one.',
    ruleExampleAudioText:
      'When you do two plus two, you skip one number and say the next one, which is four.',
    primaryFacts: [
      { addend1: 2, addend2: 2, sum: 4 },
      { addend1: 3, addend2: 2, sum: 5 },
      { addend1: 4, addend2: 2, sum: 6 },
      { addend1: 5, addend2: 2, sum: 7 },
    ],
    commutativeFacts: [
      { addend1: 2, addend2: 3, sum: 5 },
      { addend1: 2, addend2: 4, sum: 6 },
      { addend1: 2, addend2: 5, sum: 7 },
    ],
    visualization: 'number-line',
  },

  // Level 4: Plus 2 (5-8)
  {
    level: 4,
    name: 'Plus 2 (5-8)',
    rule: 'When you plus two, you skip one number and say the next one.',
    ruleAudioText: 'When you plus two, you skip one number and say the next one.',
    ruleExampleAudioText:
      'When you do five plus two, you skip one number and say the next one, which is seven.',
    primaryFacts: [
      { addend1: 5, addend2: 2, sum: 7 },
      { addend1: 6, addend2: 2, sum: 8 },
      { addend1: 7, addend2: 2, sum: 9 },
      { addend1: 8, addend2: 2, sum: 10 },
    ],
    commutativeFacts: [
      { addend1: 2, addend2: 5, sum: 7 },
      { addend1: 2, addend2: 6, sum: 8 },
      { addend1: 2, addend2: 7, sum: 9 },
      { addend1: 2, addend2: 8, sum: 10 },
    ],
    visualization: 'number-line',
  },

  // Level 5: Doubles (1-5) — no commutative facts, doubles are their own turnaround
  {
    level: 5,
    name: 'Doubles (1-5)',
    rule: 'A number plus itself equals double that number.',
    ruleAudioText: 'A number plus itself equals double that number.',
    primaryFacts: [
      { addend1: 1, addend2: 1, sum: 2 },
      { addend1: 2, addend2: 2, sum: 4 },
      { addend1: 3, addend2: 3, sum: 6 },
      { addend1: 4, addend2: 4, sum: 8 },
      { addend1: 5, addend2: 5, sum: 10 },
    ],
    commutativeFacts: [],
  },

  // Level 6: Plus 0 (1-4)
  {
    level: 6,
    name: 'Plus 0 (1-4)',
    rule: 'Any number plus zero stays the same number.',
    ruleAudioText: 'Any number plus zero stays the same number.',
    ruleExampleAudioText:
      'When you do one plus zero, the number stays the same. It\'s still one.',
    primaryFacts: [
      { addend1: 1, addend2: 0, sum: 1 },
      { addend1: 2, addend2: 0, sum: 2 },
      { addend1: 3, addend2: 0, sum: 3 },
      { addend1: 4, addend2: 0, sum: 4 },
    ],
    commutativeFacts: [
      { addend1: 0, addend2: 1, sum: 1 },
      { addend1: 0, addend2: 2, sum: 2 },
      { addend1: 0, addend2: 3, sum: 3 },
      { addend1: 0, addend2: 4, sum: 4 },
    ],
    visualization: 'number-line',
  },

  // Level 7: Plus 0 (5-9)
  {
    level: 7,
    name: 'Plus 0 (5-9)',
    rule: 'Any number plus zero stays the same number.',
    ruleAudioText: 'Any number plus zero stays the same number.',
    ruleExampleAudioText:
      'When you do five plus zero, the number stays the same. It\'s still five.',
    primaryFacts: [
      { addend1: 5, addend2: 0, sum: 5 },
      { addend1: 6, addend2: 0, sum: 6 },
      { addend1: 7, addend2: 0, sum: 7 },
      { addend1: 8, addend2: 0, sum: 8 },
      { addend1: 9, addend2: 0, sum: 9 },
    ],
    commutativeFacts: [
      { addend1: 0, addend2: 5, sum: 5 },
      { addend1: 0, addend2: 6, sum: 6 },
      { addend1: 0, addend2: 7, sum: 7 },
      { addend1: 0, addend2: 8, sum: 8 },
      { addend1: 0, addend2: 9, sum: 9 },
    ],
    visualization: 'number-line',
  },

  // Level 8: Plus 3 (2-5)
  {
    level: 8,
    name: 'Plus 3 (2-5)',
    rule: 'When you plus three, you skip two numbers and say the next one.',
    ruleAudioText: 'When you plus three, you skip two numbers and say the next one.',
    ruleExampleAudioText:
      'When you do two plus three, you skip two numbers and say the next one, which is five.',
    primaryFacts: [
      { addend1: 2, addend2: 3, sum: 5 },
      { addend1: 3, addend2: 3, sum: 6 },
      { addend1: 4, addend2: 3, sum: 7 },
      { addend1: 5, addend2: 3, sum: 8 },
    ],
    commutativeFacts: [
      { addend1: 3, addend2: 2, sum: 5 },
      { addend1: 3, addend2: 4, sum: 7 },
      { addend1: 3, addend2: 5, sum: 8 },
    ],
    visualization: 'number-line',
  },

  // Level 9: Plus 4 (2-5)
  {
    level: 9,
    name: 'Plus 4 (2-5)',
    rule: 'When you plus four, you skip three numbers and say the next one.',
    ruleAudioText: 'When you plus four, you skip three numbers and say the next one.',
    ruleExampleAudioText:
      'When you do two plus four, you skip three numbers and say the next one, which is six.',
    primaryFacts: [
      { addend1: 2, addend2: 4, sum: 6 },
      { addend1: 3, addend2: 4, sum: 7 },
      { addend1: 4, addend2: 4, sum: 8 },
      { addend1: 5, addend2: 4, sum: 9 },
    ],
    commutativeFacts: [
      { addend1: 4, addend2: 2, sum: 6 },
      { addend1: 4, addend2: 3, sum: 7 },
      { addend1: 4, addend2: 5, sum: 9 },
    ],
    visualization: 'number-line',
  },

  // Level 10: Plus 3 (3-7)
  {
    level: 10,
    name: 'Plus 3 (3-7)',
    rule: 'When you plus three, you skip two numbers and say the next one.',
    ruleAudioText: 'When you plus three, you skip two numbers and say the next one.',
    ruleExampleAudioText:
      'When you do three plus four, you skip two numbers and say the next one, which is seven.',
    primaryFacts: [
      { addend1: 3, addend2: 4, sum: 7 },
      { addend1: 3, addend2: 5, sum: 8 },
      { addend1: 3, addend2: 6, sum: 9 },
      { addend1: 3, addend2: 7, sum: 10 },
    ],
    commutativeFacts: [
      { addend1: 4, addend2: 3, sum: 7 },
      { addend1: 5, addend2: 3, sum: 8 },
      { addend1: 6, addend2: 3, sum: 9 },
      { addend1: 7, addend2: 3, sum: 10 },
    ],
    visualization: 'number-line',
  },

  // Level 11: Make a Ten
  {
    level: 11,
    name: 'Make a Ten',
    rule: 'These pairs always make ten.',
    ruleAudioText: 'These pairs always make ten.',
    primaryFacts: [
      { addend1: 1, addend2: 9, sum: 10 },
      { addend1: 2, addend2: 8, sum: 10 },
      { addend1: 3, addend2: 7, sum: 10 },
      { addend1: 4, addend2: 6, sum: 10 },
      { addend1: 5, addend2: 5, sum: 10 },
    ],
    commutativeFacts: [
      { addend1: 9, addend2: 1, sum: 10 },
      { addend1: 8, addend2: 2, sum: 10 },
      { addend1: 7, addend2: 3, sum: 10 },
      { addend1: 6, addend2: 4, sum: 10 },
    ],
  },
];
